import { useEffect } from "react";
import { Check, X } from "lucide-react";
import { rgbFloatsToHex } from "../lib/color";
import { ColorOut } from "../types";
import { useDesign } from "../state/designStore";
import { ColorPickerPanel } from "./ColorPickerPanel";

export function ColorPickerModal({
  title,
  value,
  presets,
  onChange,
  onClear,
  onClose,
}: {
  title: string;
  value: string | null;
  presets: ColorOut[];
  onChange: (hex: string) => void;
  onClear?: () => void; // omit to make the color required (e.g. main text color)
  onClose: () => void;
}) {
  const currentHex = value ?? (presets[0] ? rgbFloatsToHex(presets[0].rgb) : "#000000");
  const { setOverlayClose } = useDesign();

  // Registers this modal's close function as the shared "something is
  // open on top of the screen" handle - App.tsx's back navigation
  // (hardware button, header arrow, all of it) checks this first and
  // closes just the modal instead of navigating the screen underneath.
  // useState's setter treats a bare function argument as an updater, so
  // it must be wrapped (`() => onClose`) to actually be stored AS the
  // state value.
  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  const pick = (hex: string) => {
    onChange(hex);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md glass-card rounded-b-none max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h2 className="font-bold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="إغلاق" className="w-8 h-8 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 flex flex-col gap-6">
          <div>
            <label className="block text-sm text-tg-hint mb-3">ألوان شائعة</label>
            <div className="grid grid-cols-5 gap-3">
              {presets.map((p) => {
                const hex = rgbFloatsToHex(p.rgb);
                const isActive = value?.toLowerCase() === hex.toLowerCase();
                return (
                  <button
                    key={p.key}
                    type="button"
                    title={p.name}
                    onClick={() => pick(hex)}
                    className="flex flex-col items-center gap-1.5"
                    aria-label={p.name}
                  >
                    <span
                      className="w-11 h-11 rounded-full border-2 flex items-center justify-center"
                      style={{ backgroundColor: hex, borderColor: isActive ? "var(--tg-theme-button-color, #5288c1)" : "transparent" }}
                    >
                      {isActive && <Check size={18} strokeWidth={3} className="text-white drop-shadow" />}
                    </span>
                    <span className="text-[10px] text-tg-hint truncate w-full text-center">{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm text-tg-hint mb-3">لون مخصص</label>
            <ColorPickerPanel value={currentHex} onChange={onChange} />
          </div>

          {onClear && value && (
            <button
              type="button"
              onClick={() => {
                onClear();
                onClose();
              }}
              className="text-red-400 text-sm"
            >
              إزالة اللون
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
